'use client';

import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cx } from '../utils/cx';
import { getFilesOrigin, resolveAssetUrl, sanitizeUrl } from '../utils/asset-url';

export type BackgroundVariant = 'color' | 'image' | 'video';
export type BackgroundOverlay = 'none' | 'sm' | 'md' | 'lg';
export type BackgroundOverlayVariant = 'gradient' | 'glass' | 'solid';
export type BackgroundPreset = 'default' | 'screen';
export type BackgroundDecoration = 'none' | 'aurora';
export type BackgroundPreload = 'auto' | 'metadata' | 'none';

export interface BackgroundProps extends HTMLAttributes<HTMLDivElement> {
  variant?: BackgroundVariant;
  preset?: BackgroundPreset;
  color?: string;
  src?: string;
  srcDark?: string;
  poster?: string;
  autoPlay?: boolean;
  overlay?: BackgroundOverlay;
  overlayVariant?: BackgroundOverlayVariant;
  overlayColor?: string;
  decoration?: BackgroundDecoration;
  blur?: boolean;
  objectFit?: 'contain' | 'cover' | 'fill' | 'scale-down';
  objectPosition?: string;
  /**
   * Native <video> preload hint. Defaults to 'auto' to preserve the previous
   * hardcoded behavior; screens with large hero videos should pass 'metadata'
   * so the file is NOT fetched during the initial render (only its metadata
   * and first frame are requested before playback starts).
   */
  preload?: BackgroundPreload;
  children: ReactNode;
  safeArea?: boolean;
  fullHeight?: boolean;
  className?: string;
  style?: CSSProperties;
}

const LIGHT_BACKGROUND_FALLBACK = 'rgb(250 250 250)';
const DARK_BACKGROUND_FALLBACK = 'rgb(9 9 11)';

function isCssColorValue(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v) return false;

  return (
    v === 'transparent' ||
    v === 'currentcolor' ||
    v === 'black' ||
    v === 'white' ||
    v.startsWith('#') ||
    v.startsWith('rgb(') ||
    v.startsWith('rgba(') ||
    v.startsWith('hsl(') ||
    v.startsWith('hsla(') ||
    v.startsWith('var(')
  );
}

function isCssGradientValue(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v) return false;

  return (
    v.startsWith('linear-gradient(') ||
    v.startsWith('radial-gradient(') ||
    v.startsWith('conic-gradient(') ||
    v.startsWith('repeating-linear-gradient(') ||
    v.startsWith('repeating-radial-gradient(') ||
    v.startsWith('repeating-conic-gradient(')
  );
}

function isTokenLike(value: string): boolean {
  return /^[a-zA-Z_$][a-zA-Z0-9_$-]*$/.test(value.trim());
}

function resolveColorStyle(value: string | undefined, fallback: string): CSSProperties {
  if (!value) return { backgroundColor: fallback };

  const trimmed = value.trim();
  if (!trimmed) return { backgroundColor: fallback };

  // Support layered CSS gradients (e.g. the aurora-style radial backdrop) by
  // keeping a solid fallback color underneath the gradient image.
  if (isCssGradientValue(trimmed)) {
    return { backgroundColor: fallback, backgroundImage: trimmed };
  }

  if (isCssColorValue(trimmed)) {
    return { backgroundColor: trimmed };
  }

  if (isTokenLike(trimmed)) {
    return { backgroundColor: `var(--${trimmed}, ${fallback})` };
  }

  return { backgroundColor: fallback };
}

function overlayOpacity(overlay: BackgroundOverlay | undefined): number {
  switch (overlay) {
    case 'none':
      return 0;
    case 'sm':
      return 0.18;
    case 'lg':
      return 0.45;
    case 'md':
    default:
      return 0.3;
  }
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mql.matches);

    setReduced(mql.matches);

    if ('addEventListener' in mql) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }

    const legacyMql = mql as MediaQueryList & {
      addListener(listener: () => void): void;
      removeListener(listener: () => void): void;
    };
    legacyMql.addListener(onChange);
    return () => legacyMql.removeListener(onChange);
  }, []);

  return reduced;
}

function useResolvedColorScheme(): 'light' | 'dark' {
  const [scheme, setScheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const root = document.documentElement;
    const readScheme = (): 'light' | 'dark' => {
      const cls = root.classList;
      if (cls.contains('dark') || cls.contains('dark-mode')) return 'dark';
      if (window.matchMedia?.('(prefers-color-scheme: dark)')?.matches) return 'dark';
      return 'light';
    };

    const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onMqlChange = () => setScheme(readScheme());

    setScheme(readScheme());

    if (mql) {
      if ('addEventListener' in mql) {
        mql.addEventListener('change', onMqlChange);
      } else {
        const legacyMql = mql as MediaQueryList & {
          addListener(listener: () => void): void;
          removeListener(listener: () => void): void;
        };
        legacyMql.addListener(onMqlChange);
      }
    }

    const observer = new MutationObserver(() => setScheme(readScheme()));
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();

      if (!mql) return;
      if ('removeEventListener' in mql) {
        mql.removeEventListener('change', onMqlChange);
        return;
      }

      const legacyMql = mql as MediaQueryList & {
        removeListener(listener: () => void): void;
      };
      legacyMql.removeListener(onMqlChange);
    };
  }, []);

  return scheme;
}

function resolveBackgroundSize(objectFit: NonNullable<BackgroundProps['objectFit']>): string {
  switch (objectFit) {
    case 'fill':
      return '100% 100%';
    case 'scale-down':
      return 'contain';
    case 'contain':
    case 'cover':
    default:
      return objectFit;
  }
}

function presetClass(preset: BackgroundPreset, fullHeight: boolean): string {
  switch (preset) {
    case 'screen':
      return 'overflow-visible min-h-screen';
    case 'default':
    default:
      return fullHeight ? 'min-h-dvh' : '';
  }
}

function auroraAnimationStyle(delay: string, reducedMotion: boolean): CSSProperties {
  return {
    animationName: reducedMotion ? 'none' : 'bgAuroraFloat',
    animationDuration: '6s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: reducedMotion ? '1' : 'infinite',
    animationDelay: reducedMotion ? '0s' : delay,
  };
}

export function Background(props: BackgroundProps) {
  const {
    variant = 'image',
    preset = 'default',
    color,
    src,
    srcDark,
    poster,
    autoPlay = true,
    overlay = 'none',
    overlayVariant = 'gradient',
    overlayColor,
    decoration = 'none',
    blur = false,
    objectFit = 'cover',
    objectPosition = 'center',
    safeArea = false,
    fullHeight = true,
    preload = 'auto',
    className,
    style,
    children,
    layoutPreset,
    motionPreset,
    motionStep,
    ...rest
  } = props as BackgroundProps & {
    layoutPreset?: unknown;
    motionPreset?: unknown;
    motionStep?: unknown;
  };

  const divProps = rest as HTMLAttributes<HTMLDivElement>;

  const scheme = useResolvedColorScheme();
  const reducedMotion = usePrefersReducedMotion();
  const filesOrigin = useMemo(() => getFilesOrigin(), []);
  const fallbackBackground = scheme === 'dark' ? DARK_BACKGROUND_FALLBACK : LIGHT_BACKGROUND_FALLBACK;
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const safeSrcLight = useMemo(
    () => sanitizeUrl(resolveAssetUrl(String(src ?? ''), filesOrigin)),
    [src, filesOrigin],
  );
  const safeSrcDark = useMemo(
    () => (srcDark ? sanitizeUrl(resolveAssetUrl(srcDark, filesOrigin)) : ''),
    [srcDark, filesOrigin],
  );

  const activeSrc = useMemo(() => {
    if (scheme === 'dark') return safeSrcDark || safeSrcLight;
    return safeSrcLight || safeSrcDark;
  }, [scheme, safeSrcDark, safeSrcLight]);

  const safePoster = useMemo(() => {
    const p = String(poster ?? '').trim();
    if (!p) return undefined;
    return sanitizeUrl(resolveAssetUrl(p, filesOrigin)) || undefined;
  }, [poster, filesOrigin]);

  const requestVideoPlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video || !activeSrc || !autoPlay || reducedMotion) return;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Ignore autoplay rejections; Safari may still require a gesture in some cases.
      });
    }
  }, [activeSrc, autoPlay, reducedMotion]);

  useEffect(() => {
    if (variant !== 'video') return;
    requestVideoPlayback();
  }, [requestVideoPlayback, variant]);

  // Re-attempt playback when the tab becomes visible again (iOS pauses media
  // in background tabs and autoplay policies can reject the first attempt).
  useEffect(() => {
    if (variant !== 'video' || !autoPlay || reducedMotion) return;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') requestVideoPlayback();
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [requestVideoPlayback, variant, autoPlay, reducedMotion]);

  const contentSafeAreaClass = safeArea
    ? 'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]'
    : undefined;

  const backgroundLayer = useMemo(() => {
    if (variant === 'color') {
      const bgStyle = resolveColorStyle(color, fallbackBackground);
      return <div aria-hidden className="absolute inset-0 z-0" style={bgStyle} />;
    }

    if (variant === 'video') {
      if (!activeSrc) {
        // No playable source: fall back to the solid color backdrop so the
        // screen never renders as fully transparent.
        return (
          <div
            aria-hidden
            className="absolute inset-0 z-0"
            style={{ backgroundColor: fallbackBackground }}
          />
        );
      }

      return (
        <video
          ref={videoRef}
          aria-hidden="true"
          className="absolute inset-0 z-0 h-full w-full"
          style={{
            objectFit,
            objectPosition,
            backgroundColor: fallbackBackground,
          }}
          src={activeSrc}
          poster={safePoster}
          muted
          playsInline
          loop
          autoPlay={autoPlay && !reducedMotion}
          preload={preload}
          disablePictureInPicture
        />
      );
    }

    // variant === 'image'
    return (
      <div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: activeSrc ? `url(${JSON.stringify(activeSrc)})` : undefined,
          backgroundSize: resolveBackgroundSize(objectFit),
          backgroundPosition: objectPosition,
          backgroundRepeat: 'no-repeat',
          backgroundColor: fallbackBackground,
        }}
      />
    );
  }, [
    variant,
    color,
    fallbackBackground,
    activeSrc,
    safePoster,
    autoPlay,
    reducedMotion,
    preload,
    objectFit,
    objectPosition,
  ]);

  const overlayOpacityValue = overlayOpacity(overlay);

  const overlayLayer = useMemo(() => {
    if (overlayOpacityValue === 0) return null;

    let overlayStyle: CSSProperties;

    if (overlayVariant === 'solid') {
      overlayStyle = resolveColorStyle(
        overlayColor,
        scheme === 'dark' ? 'rgb(9 9 11)' : 'rgb(24 24 27)',
      );
      // Convert the resolved background color into a uniform translucent veil.
      if (overlayStyle.backgroundColor) {
        overlayStyle.opacity = overlayOpacityValue;
      }
    } else if (overlayVariant === 'glass') {
      overlayStyle = {
        backgroundColor: overlayColor ?? (scheme === 'dark' ? 'rgb(9 9 11)' : 'rgb(255 255 255)'),
        opacity: overlayOpacityValue,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      };
    } else {
      // gradient: darken the media edges toward the bottom for legibility.
      overlayStyle = {
        backgroundImage:
          overlayColor ??
          (scheme === 'dark'
            ? 'linear-gradient(to bottom, rgb(9 9 11 / 0) 35%, rgb(9 9 11 / 1) 100%)'
            : 'linear-gradient(to bottom, rgb(0 0 0 / 0) 35%, rgb(0 0 0 / 1) 100%)'),
        opacity: overlayOpacityValue,
      };
    }

    return <div aria-hidden className="pointer-events-none absolute inset-0 z-10" style={overlayStyle} />;
  }, [overlayOpacityValue, overlayVariant, overlayColor, scheme]);

  const decorationLayer = useMemo(() => {
    if (decoration !== 'aurora') return null;

    return (
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      >
        <div
          className="absolute -top-1/4 left-1/4 h-3/4 w-3/4 rounded-full opacity-30 blur-3xl"
          style={{
            backgroundColor: scheme === 'dark' ? 'rgb(56 189 248)' : 'rgb(129 140 248)',
            ...auroraAnimationStyle('0s', reducedMotion),
          }}
        />
        <div
          className="absolute -bottom-1/4 right-1/4 h-2/3 w-2/3 rounded-full opacity-25 blur-3xl"
          style={{
            backgroundColor: scheme === 'dark' ? 'rgb(168 85 247)' : 'rgb(244 114 182)',
            ...auroraAnimationStyle('-3s', reducedMotion),
          }}
        />
      </div>
    );
  }, [decoration, scheme, reducedMotion]);

  return (
    <div
      {...divProps}
      className={cx(
        'relative isolate w-full overflow-hidden',
        presetClass(preset, fullHeight),
        contentSafeAreaClass,
        blur && 'blur-sm',
        className,
      )}
      style={{ ...divProps.style, ...style }}
    >
      {decorationLayer}
      {backgroundLayer}
      {overlayLayer}
      {children && (
        <div className={cx('relative z-20', contentSafeAreaClass)}>{children}</div>
      )}
    </div>
  );
}

export default Background;